import React, { ReactNode, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { FormattedMessage, useIntl } from "react-intl";
import { Formik, FormikHelpers } from "formik";
import { apiToFormikErrors } from "../../utils/formHelpers";
import { ObjectSchema } from "yup";
import { toast } from "react-toastify";
import { ModalProps } from "react-bootstrap";
import LoadingPacman from "../layout/LoadingPacman";

export type SimpleEntityUpsertModalProps = {
    showModal: boolean;
    entityName: string;
    handleClose: () => void;
    entity?: { id: number; name: string; comment: string } | null;
    updateGridOnSave: (object: any, isNew: boolean) => void;
    saveHandler: (_values: any, objectId?: number, confirmationResponse?: any) => any;
    confirmationHandler?: (_values: any, objectId?: number) => any;
    confirmationModalCondition?: (value: any, entity?: any) => boolean;
    confirmationModalBody?: React.FC<any>;
    validationSchema: ObjectSchema<any>;
    modalProps?: ModalProps;
    formTransformer?: (entity: any) => any;
};

export const SimpleEntityUpsertModal: React.FC<
    SimpleEntityUpsertModalProps & { children: ReactNode }
> = (props) => {
    const {
        entity,
        showModal,
        confirmationHandler,
        confirmationModalCondition,
        handleClose,
        confirmationModalBody = () => <>NoBody</>,
        updateGridOnSave,
        validationSchema,
        children,
        entityName,
        saveHandler,
        modalProps,
        formTransformer = (object: any) => object,
    } = props;
    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState(false);
    const [values, setValues] = useState<any>();
    const [auxFormikHelpers, setAuxFormikHelpers] = useState<FormikHelpers<any>>();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationResponse, setConfirmationResponse] = useState<any>();

    const onSubmit = async (_values: any, formikHelpers: FormikHelpers<any>) => {
        for (const key in _values) {
            if (_values[key] === "") _values[key] = undefined;
        }
        setLoading(true);

        if (
            (confirmationHandler &&
                confirmationModalCondition &&
                confirmationModalCondition(_values, entity)) ||
            (confirmationHandler && !confirmationModalCondition)
        ) {
            setValues(_values);
            setAuxFormikHelpers(formikHelpers);
            try {
                setConfirmationResponse(await confirmationHandler(_values, entity?.id));
                setLoading(false);
                setShowConfirmationModal(true);
            } catch (error: any) {
                toast.error(
                    error?.response?.data?.errors[0]?.message ||
                        formatMessage({ id: "common.notification.error" })
                );

                apiToFormikErrors(error?.response, formikHelpers);
            }
        } else {
            try {
                const res = await saveHandler(_values, entity?.id);
                const newEntity = { ...entity, ...res };
                await updateGridOnSave(newEntity, !entity?.id);
                setLoading(false);
                toast.success(formatMessage({ id: "common.notification.success" }));
                handleClose();
            } catch (error: any) {
                toast.error(
                    error?.response?.data?.errors[0]?.message ||
                        formatMessage({ id: "common.notification.error" })
                );
                apiToFormikErrors(error?.response, formikHelpers);
            }
        }
        setLoading(false);
    };

    const onConfirmation = async () => {
        setLoading(true);
        try {
            const res = await saveHandler(values, entity?.id, confirmationResponse);
            await updateGridOnSave(res, !entity?.id);
            toast.success(formatMessage({ id: "common.notification.success" }));
            setLoading(false);
            setShowConfirmationModal(false);
            handleClose();
        } catch (error: any) {
            toast.error(
                error?.response?.data?.errors[0]?.message ||
                    formatMessage({ id: "common.notification.error" })
            );
            if (auxFormikHelpers) apiToFormikErrors(error?.response, auxFormikHelpers);
        }
        setLoading(false);
    };

    return (
        <>
            <Modal
                centered
                show={showModal}
                onHide={handleClose}
                dialogClassName="simpleEntityGrid-modal"
                size={modalProps?.size || "lg"}
                {...modalProps}
            >
                <LoadingPacman loading={loading} />
                <Formik
                    initialValues={entity ? formTransformer(entity) : validationSchema.getDefault()}
                    onSubmit={onSubmit}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                >
                    {(formikProps) => (
                        <form onSubmit={formikProps.handleSubmit}>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    {entity ? (
                                        <>
                                            <FormattedMessage id="common.action.edit" />{" "}
                                            {entity?.name}
                                        </>
                                    ) : (
                                        <>
                                            <FormattedMessage id="common.action.add" /> {entityName}
                                        </>
                                    )}
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>{children}</Modal.Body>
                            <Modal.Footer>
                                <Button variant="primary" disabled={loading} type="submit">
                                    <FormattedMessage id="common.action.save" />
                                </Button>
                            </Modal.Footer>
                        </form>
                    )}
                </Formik>
            </Modal>

            <Modal centered style={{ background: "rgba(0,0,0,0.5)" }} show={showConfirmationModal}>
                <LoadingPacman loading={loading} />
                <Modal.Header closeButton onClick={() => setShowConfirmationModal(false)}>
                    <Modal.Title>
                        <FormattedMessage id="common.action.confirmation" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {React.createElement(confirmationModalBody, {
                        data: confirmationResponse,
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={onConfirmation}>
                        <FormattedMessage id="common.action.buy" />
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
